import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Accordion, AccordionButton, AccordionItem, AccordionPanel, Box, Button, Input, Text, Textarea } from '@chakra-ui/react';
import { Add, Remove, WhatsApp, Instagram, Place } from '@mui/icons-material';

import Breadcrumb from '../components/layout/Breadcrumb';
import ProductGrid from '../components/product/ProductGrid';
import useCatalog from '../hooks/useCatalog';
import { STORE, whatsappLink } from '../config/store';

// Static pages (design-system.md §5.7): About, Contact, FAQs, policies and 404.
// The copy repeats what the store already promises elsewhere (free delivery, 30-day returns, payments);
// review the policy wording before going live.

const condensed = { fontWeight: 700, textTransform: 'uppercase', sx: { fontStretch: '75%' } };

// Grey band with a huge condensed title ("ABOUT US")
const Hero = ({ title, kicker }) => (
    <Box bg='raano.surface' px={{ base: '16px', md: '40px' }} py={{ base: '40px', md: '72px' }} >
        {kicker && <Text fontSize='12px' fontWeight={700} letterSpacing='0.12em' textTransform='uppercase' color='raano.muted' mb='8px' >{kicker}</Text>}
        <Text as='h1' fontSize={{ base: '48px', md: '96px' }} lineHeight={0.9} {...condensed} >{title}</Text>
    </Box>
);

const Prose = ({ children }) => (
    <Box maxWidth='680px' px={{ base: '16px', md: '40px' }} py={{ base: '28px', md: '48px' }} fontSize='15px' lineHeight='1.7' color='blackAlpha.800'
        sx={{ 'h2': { fontSize: '20px', fontWeight: 700, textTransform: 'uppercase', fontStretch: '75%', color: 'raano.black', mt: '28px', mb: '8px' }, 'p + p': { mt: '12px' }, 'ul': { pl: '20px', mt: '8px' }, 'li': { mt: '4px' } }} >
        {children}
    </Box>
);

export const About = () => (
    <Box>
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'About us' }]} />
        <Hero title='About us' kicker={STORE.name} />
        <Prose>
            <Text fontSize='18px' fontWeight={600} color='raano.black' >Raano is built for one thing: clothes that fit right and last.</Text>
            <p>We're a men's clothing store in Bhuj, Gujarat. Shirts, tees, jeans, jackets, suits and blazers, picked for fit and fabric first, so they keep looking good past the season.</p>
            <p>Shop online with free delivery and 30-day returns, or visit the store to try the fit in person. Not sure about a size? Message us on WhatsApp and we'll help you choose.</p>
            <h2>Visit the store</h2>
            <p>Come in, try things on, and we'll alter what needs altering. <Box as='a' href={STORE.mapsUrl} target='_blank' rel='noreferrer' textDecoration='underline' >Get directions</Box>.</p>
        </Prose>
    </Box>
);

export const Contact = () => {
    const [name, setName] = useState('');
    const [message, setMessage] = useState('');
    const send = (e) => {
        e.preventDefault();
        window.open(whatsappLink(`Hi Raano, this is ${name.trim() || 'a customer'}. ${message.trim()}`), '_blank', 'noreferrer');
    };
    return (
        <Box>
            <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Contact us' }]} />
            <Hero title='Contact us' />
            <Box display='flex' flexDirection={{ base: 'column', md: 'row' }} gap={{ base: '28px', md: '64px' }} px={{ base: '16px', md: '40px' }} py={{ base: '28px', md: '48px' }} >
                <Box as='form' onSubmit={send} flex={1} maxWidth='560px' >
                    <Text fontSize='15px' color='raano.muted' mb='18px' >Got something to say? We're listening, and we'll reply on WhatsApp.</Text>
                    <Text as='label' htmlFor='c-name' fontSize='14px' fontWeight={600} >Name</Text>
                    <Input id='c-name' mt='6px' height='48px' borderRadius='4px' value={name} onChange={(e) => setName(e.target.value)} />
                    <Text as='label' htmlFor='c-msg' display='block' mt='16px' fontSize='14px' fontWeight={600} >Message</Text>
                    <Textarea id='c-msg' mt='6px' minHeight='140px' borderRadius='4px' value={message} onChange={(e) => setMessage(e.target.value)} isRequired />
                    <Button type='submit' mt='18px' variant='ink' height='48px' px='32px' fontSize='16px' leftIcon={<WhatsApp fontSize='small' />} >Send on WhatsApp</Button>
                </Box>
                <Box width={{ base: '100%', md: '360px' }} >
                    <Box border='1px solid' borderColor='raano.border' borderRadius='8px' p='20px' >
                        <Text fontSize='16px' {...condensed} mb='12px' >Customer care</Text>
                        {[[WhatsApp, STORE.whatsappDisplay, whatsappLink('Hi Raano!')], [Instagram, `@${STORE.instagramHandle}`, STORE.instagramUrl], [Place, 'Visit our store', STORE.mapsUrl]].map(([Icon, label, href]) => (
                            <Box key={label} as='a' href={href} target='_blank' rel='noreferrer' display='flex' alignItems='center' gap='10px' py='8px' fontSize='14px' _hover={{ textDecoration: 'underline' }} ><Icon sx={{ fontSize: 18 }} />{label}</Box>
                        ))}
                        <Text mt='10px' fontSize='13px' color='raano.muted' >We usually reply within a few hours, 10am–8pm.</Text>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

const FAQ = [
    {
        group: 'Orders', items: [
            ['How do I know my order has been confirmed?', 'You get an order confirmation email as soon as payment succeeds, and the order appears under My orders.'],
            ['Can I cancel my order?', 'Yes, while it is still being prepared. Open My orders and tap Cancel order. Once it has shipped, you can return it instead.'],
            ['How do I track my order?', 'My orders shows the status of every order: Preparing, On the way or Delivered. We also email you when the status changes.'],
        ]
    },
    {
        group: 'Delivery', items: [
            ['Do you charge for delivery?', 'No. Delivery is free on every order.'],
            ['How long does delivery take?', 'Usually 3–5 days. Check your PIN code on any product page to confirm we deliver there.'],
        ]
    },
    {
        group: 'Returns & exchanges', items: [
            ['What is your return policy?', 'You can return or exchange unworn items with their tags within 30 days of delivery.'],
            ['How do I start a return?', 'Message us on WhatsApp with your order number and we will arrange it.'],
        ]
    },
    {
        group: 'Payments & sizing', items: [
            ['Which payment methods do you accept?', 'UPI, cards, netbanking and wallets. Payments are processed securely; we never see your card details.'],
            ['How do I find my size?', 'Each product page has a size chart. Between sizes? Ask us on WhatsApp and we will help.'],
        ]
    },
];

export const Faqs = () => (
    <Box>
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'FAQs' }]} />
        <Box maxWidth='820px' mx='auto' px={{ base: '16px', md: '24px' }} py={{ base: '28px', md: '48px' }} >
            <Text as='h1' textAlign='center' fontSize={{ base: '40px', md: '56px' }} {...condensed} >FAQs</Text>
            {FAQ.map(section => (
                <Box key={section.group} mt={{ base: '24px', md: '36px' }} >
                    <Text as='h2' fontSize={{ base: '22px', md: '28px' }} {...condensed} mb='4px' >{section.group}</Text>
                    <Accordion allowMultiple >
                        {section.items.map(([q, a]) => (
                            <AccordionItem key={q} borderTop='none' borderBottom='1px solid' borderColor='raano.border' >
                                {({ isExpanded }) => (
                                    <>
                                        <AccordionButton px={0} py='16px' _hover={{ bg: 'transparent' }} >
                                            <Text flex={1} textAlign='left' fontSize='13px' fontWeight={600} textTransform='uppercase' >{q}</Text>
                                            {isExpanded ? <Remove sx={{ fontSize: 18 }} /> : <Add sx={{ fontSize: 18 }} />}
                                        </AccordionButton>
                                        <AccordionPanel px={0} pt={0} pb='16px' fontSize='14px' lineHeight='1.6' color='blackAlpha.800' >{a}</AccordionPanel>
                                    </>
                                )}
                            </AccordionItem>
                        ))}
                    </Accordion>
                </Box>
            ))}
        </Box>
    </Box>
);

const POLICIES = {
    'privacy-policy': {
        title: 'Privacy policy', body: <>
            <p>{STORE.name} ("Raano", "we") runs this store. This policy explains what we collect when you shop with us and how we use it.</p>
            <h2>What we collect</h2>
            <ul><li>Account details: your name, email address and password (stored encrypted).</li><li>Order details: delivery address, phone number and the email for order updates.</li><li>Your cart, wishlist, ratings and reviews.</li></ul>
            <h2>How we use it</h2>
            <p>To deliver your orders, send order updates by email or WhatsApp, and keep your cart and wishlist across devices. We don't sell your data.</p>
            <h2>Payments</h2>
            <p>Payments are handled by our payment partners (Razorpay or Stripe). We never see or store your card details.</p>
            <h2>Contact</h2>
            <p>Questions about your data? Message us on WhatsApp at {STORE.whatsappDisplay}.</p>
        </>
    },
    'refund-policy': {
        title: 'Shipping & returns', body: <>
            <h2>Shipping</h2>
            <p>Delivery is free on every order and usually takes 3–5 days. You'll get an email when your order ships and when it's delivered.</p>
            <h2>Returns & exchanges</h2>
            <p>You can return or exchange items within 30 days of delivery, as long as they're unworn and still have their tags.</p>
            <p>To start a return, message us on WhatsApp with your order number. Refunds go back to the original payment method once we receive the item.</p>
            <h2>Cancellations</h2>
            <p>You can cancel an order from My orders while it's still being prepared.</p>
        </>
    },
};

export const Policy = () => {
    const { slug } = useParams();
    const policy = POLICIES[slug];
    if (!policy) return <NotFound />;
    return (
        <Box>
            <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: policy.title }]} />
            <Box display='flex' justifyContent='center' >
                <Box width='100%' maxWidth='760px' >
                    <Text as='h1' textAlign='center' fontSize={{ base: '36px', md: '48px' }} {...condensed} pt={{ base: '28px', md: '48px' }} >{policy.title}</Text>
                    <Text textAlign='center' fontSize='13px' color='raano.muted' mt='4px' >Last updated: October 2026</Text>
                    <Prose>{policy.body}</Prose>
                </Box>
            </Box>
        </Box>
    );
};

export const NotFound = () => {
    const navigate = useNavigate();
    const { products } = useCatalog();
    return (
        <Box>
            <Box textAlign='center' px='16px' py={{ base: '56px', md: '80px' }} >
                <Text as='h1' fontSize={{ base: '36px', md: '48px' }} {...condensed} >Page not found</Text>
                <Text mt='8px' fontSize='15px' color='raano.muted' >The link may be incorrect, or the page has been removed.</Text>
                <Button mt='24px' variant='accent' height='44px' px='28px' onClick={() => navigate('/')} >Continue shopping</Button>
            </Box>
            {products.length > 0 &&
                <Box px={{ base: '12px', md: '40px' }} pb={{ base: '32px', md: '56px' }} >
                    <Text fontSize={{ base: '22px', md: '28px' }} {...condensed} mb='16px' >You may also like</Text>
                    <ProductGrid products={[...products].reverse().slice(0, 4)} />
                </Box>}
        </Box>
    );
};
